import React, { useState } from "react";
import { ChevronDown, ChevronRight, Hammer } from "lucide-react";
import { apiFetch } from "../../pages/marketplace/api";

/**
 * Stock on the product card: one number for a simple product, or a total that opens
 * into each type. Counts are edited where they are read — no drawer, no second page.
 */
export const ProductStock = ({ product, threshold = 3, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);   // { key, value }
  const [saving, setSaving] = useState(false);

  const types = (product.variants || []).filter(v => (v.name || "").trim());
  const madeToOrder = product.made_to_order || product.print_on_demand;
  const tracked = product.inventory?.track;

  const save = async (variantName, value) => {
    const quantity = Math.max(0, parseInt(value, 10) || 0);
    setSaving(true);
    try {
      const res = await apiFetch(`/vendor/products/${product.id}/stock`, {
        method: "PATCH",
        body: JSON.stringify({ quantity, variant_name: variantName || null }),
      });
      onChange?.({ ...product, inventory: res.inventory, variants: res.variants });
    } catch (e) {
      console.error("stock save failed", e);
    } finally {
      setSaving(false);
      setDraft(null);
    }
  };

  const NumberCell = ({ cellKey, value, variantName, low }) => {
    const editing = draft?.key === cellKey;
    if (editing) {
      return (
        <input
          autoFocus
          type="number"
          min="0"
          className="vp-stock-input"
          value={draft.value}
          disabled={saving}
          onChange={e => setDraft({ key: cellKey, value: e.target.value })}
          onBlur={() => save(variantName, draft.value)}
          onKeyDown={e => {
            if (e.key === "Enter") save(variantName, draft.value);
            if (e.key === "Escape") setDraft(null);
          }}
          data-testid={`stock-input-${cellKey}`}
        />
      );
    }
    return (
      <button
        className={`vp-stock-value ${low ? "low" : ""} ${value === 0 ? "zero" : ""}`}
        onClick={(e) => { e.stopPropagation(); setDraft({ key: cellKey, value: String(value) }); }}
        title="Click to edit"
        data-testid={`stock-value-${cellKey}`}
      >
        {value}
      </button>
    );
  };

  if (madeToOrder) {
    return (
      <div className="vp-stock-row" data-testid={`stock-row-${product.id}`}>
        <span className="vp-stock-label">Stock</span>
        <span className="vp-stock-mto"><Hammer size={11} /> Made to order</span>
      </div>
    );
  }

  if (types.length) {
    const total = types.reduce((s, v) => s + (parseInt(v.stock, 10) || 0), 0);
    const anyLow = types.some(v => (parseInt(v.stock, 10) || 0) <= threshold);
    return (
      <div data-testid={`stock-row-${product.id}`}>
        <div className="vp-stock-row">
          <span className="vp-stock-label">Stock</span>
          <button
            className="vp-stock-expand"
            onClick={() => setOpen(o => !o)}
            data-testid={`stock-expand-${product.id}`}
          >
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <strong className={anyLow ? "low" : ""}>{total}</strong>
            <span>· {types.length} type{types.length === 1 ? "" : "s"}</span>
          </button>
        </div>
        {open && (
          <div className="vp-stock-types">
            {types.map((v, i) => {
              const stock = parseInt(v.stock, 10) || 0;
              return (
                <div className="vp-stock-type" key={v.name || i}>
                  <span className="vp-stock-type-name" title={v.name}>{v.name}</span>
                  {v.sku && <span className="vp-stock-sku">{v.sku}</span>}
                  <NumberCell
                    cellKey={`${product.id}-${i}`}
                    value={stock}
                    variantName={v.name}
                    low={stock <= threshold}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const qty = parseInt(product.inventory?.quantity, 10) || 0;
  const editingSimple = draft?.key === product.id;
  return (
    <div className="vp-stock-row" data-testid={`stock-row-${product.id}`}>
      <span className="vp-stock-label">Stock</span>
      {tracked || editingSimple ? (
        <NumberCell cellKey={product.id} value={qty} variantName={null}
                    low={tracked && qty <= threshold} />
      ) : (
        <button
          className="vp-stock-untracked"
          onClick={() => setDraft({ key: product.id, value: "0" })}
          data-testid={`stock-untracked-${product.id}`}
        >
          Not counted
        </button>
      )}
    </div>
  );
};

export default ProductStock;
